import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AgentChatError,
  AgentEvent,
  AgentMeta,
  AgentTransport,
  ChatMessage,
  MemoryConfirmation,
  TodoItem,
  ToolCall,
} from "../types";
import type { Locale } from "../i18n/types";
import { applyEventToMessages } from "./applyEvent";
import { formatCost } from "../utils/formatCost";
import { formatDuration } from "../utils/formatDuration";
import { formatTokens } from "../utils/formatTokens";

export interface UnreadState {
  counts: Record<string, number>;
  activity: Record<string, boolean>;
}

export interface UseAgentChatOptions {
  transport: AgentTransport;
  agent: AgentMeta;
  agentId: string;
  sessionId: string;
  /** 所有需要追踪事件的 session ID（含活跃 session） */
  allSessionIds?: string[];
  maxMessages: number;
  locale: Locale;
  costTpl: string;
  onError?: (err: AgentChatError) => void;
  onAgentExited?: (sessionId: string, reason?: string) => void;
  /** 非活跃 session 收到消息时通知上层未读状态 */
  onUnreadChange?: (state: UnreadState) => void;
}

interface SessionCacheEntry {
  messages: ChatMessage[];
  cost: number;
  duration: number;
  tokens: number;
  todos: TodoItem[];
}

interface UseAgentChatReturn {
  messages: ChatMessage[];
  todos: TodoItem[];
  memoryConfirmations: MemoryConfirmation[];
  hasMoreHistory: boolean;
  loadingHistory: boolean;
  firstItemIndex: number;
  exited: boolean;
  send: (content: string, attachments?: string[]) => void;
  approveTool: (toolUseId: string, approved: boolean) => void;
  approveMemory: (toolUseId: string, approved: boolean) => void;
  interrupt: () => void;
  loadMoreHistory: () => void;
}

const START_INDEX = 100_000;

export function useAgentChat(opts: UseAgentChatOptions): UseAgentChatReturn {
  const {
    transport,
    agent,
    agentId,
    sessionId,
    allSessionIds,
    maxMessages,
    locale,
    costTpl,
    onError,
    onAgentExited,
    onUnreadChange,
  } = opts;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [memoryConfirmations, setMemoryConfirmations] = useState<MemoryConfirmation[]>([]);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [firstItemIndex, setFirstItemIndex] = useState(START_INDEX);
  const [exited, setExited] = useState(false);

  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const todosRef = useRef(todos);
  todosRef.current = todos;
  const totalCostRef = useRef(0);
  const totalDurationRef = useRef(0);
  const ctxUsedRef = useRef(0);
  const sessionCacheRef = useRef<Map<string, SessionCacheEntry>>(new Map());
  const prevSessionRef = useRef<string | null>(null);
  const isInitialLoadRef = useRef(true);

  // ─── 消息数量上限：保留最新 maxMessages 条 ───
  useEffect(() => {
    if (messages.length > maxMessages) {
      setMessages((prev) => prev.slice(prev.length - maxMessages));
      setHasMoreHistory(true);
    }
  }, [messages.length, maxMessages]);

  // ─── 会话切换 + 缓存恢复 / 历史加载 ───
  useEffect(() => {
    // 保存离开 session 的状态到缓存
    if (prevSessionRef.current && messagesRef.current.length > 0) {
      sessionCacheRef.current.set(prevSessionRef.current, {
        messages: [...messagesRef.current],
        cost: totalCostRef.current,
        duration: totalDurationRef.current,
        tokens: ctxUsedRef.current,
        todos: [...todosRef.current],
      });
    }
    prevSessionRef.current = sessionId;

    setFirstItemIndex(START_INDEX);
    setMemoryConfirmations([]);
    setExited(false);

    if (!sessionId) {
      setMessages([]);
      setTodos([]);
      return;
    }

    const cached = sessionCacheRef.current.get(sessionId);
    if (cached) {
      setMessages(cached.messages);
      setTodos(cached.todos);
      totalCostRef.current = cached.cost;
      totalDurationRef.current = cached.duration;
      ctxUsedRef.current = cached.tokens;
      setHasMoreHistory(false);
      isInitialLoadRef.current = false;
      return;
    }

    // 无缓存：从后端加载
    setMessages([]);
    setTodos([]);
    setHasMoreHistory(false);
    isInitialLoadRef.current = true;
    totalCostRef.current = 0;
    totalDurationRef.current = 0;
    ctxUsedRef.current = 0;

    const unsub = transport.subscribeHistory(sessionId, ({ messages: history, has_more }) => {
      const historyMsgs: ChatMessage[] = history
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => {
          const ts = Date.parse(m.created_at) || Date.now();
          return {
            id: m.id,
            role: m.role as "user" | "assistant",
            content: m.content,
            timestamp: ts,
            created_at: ts,
          };
        });
      if (historyMsgs.length > 0) {
        historyMsgs.reverse();
        setFirstItemIndex((prev) => prev - historyMsgs.length);
        setMessages((prev) => [...historyMsgs, ...prev]);
      }
      setHasMoreHistory(has_more);
      setLoadingHistory(false);
      isInitialLoadRef.current = false;
    });

    try {
      transport.queryMessages(agentId, sessionId, { limit: 30 });
    } catch (e) {
      onError?.({ kind: "history", cause: e instanceof Error ? e : new Error(String(e)) });
    }

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, agentId]);

  // ─── 事件订阅 ───
  const handleEvent = useCallback(
    (event: AgentEvent) => {
      if (typeof event === "string") return;

      if ("TextDelta" in event) {
        const delta = event.TextDelta.content;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return [
              ...prev.slice(0, -1),
              { ...last, content: last.content + delta, streaming: true },
            ];
          }
          return [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: "assistant",
              content: delta,
              streaming: true,
              timestamp: Date.now(),
            },
          ];
        });
      } else if ("ThinkingDelta" in event) {
        const delta = event.ThinkingDelta.content;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return [
              ...prev.slice(0, -1),
              { ...last, thinking: (last.thinking ?? "") + delta, streaming: true },
            ];
          }
          return [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: "assistant",
              content: "",
              thinking: delta,
              streaming: true,
              timestamp: Date.now(),
            },
          ];
        });
      } else if ("MessageComplete" in event) {
        const complete = event.MessageComplete.content;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && last.streaming) {
            return [...prev.slice(0, -1), { ...last, streaming: false }];
          }
          if (complete) {
            return [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: "assistant",
                content: complete,
                streaming: false,
                timestamp: Date.now(),
              },
            ];
          }
          return prev;
        });
      } else if ("TodoUpdate" in event) {
        setTodos(
          event.TodoUpdate.todos.map((td) => ({
            id: td.id,
            content: td.content,
            status: (td.status === "completed"
              ? "completed"
              : td.status === "in_progress"
              ? "in_progress"
              : "pending") as TodoItem["status"],
            priority: td.priority,
          })),
        );
      } else if ("ToolUse" in event) {
        const tool = event.ToolUse;
        const toolCall: ToolCall = {
          toolUseId: tool.tool_use_id,
          toolName: tool.tool_name,
          input: tool.input,
          status: "pending",
          needsApproval: false,
        };
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return [
              ...prev.slice(0, -1),
              { ...last, toolCalls: [...(last.toolCalls ?? []), toolCall] },
            ];
          }
          return [
            ...prev,
            { id: crypto.randomUUID(), role: "assistant" as const, content: "", toolCalls: [toolCall], timestamp: Date.now() },
          ];
        });
      } else if ("ToolResult" in event) {
        const result = event.ToolResult;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && last.toolCalls) {
            const updated = last.toolCalls.map((tc) =>
              tc.toolUseId === result.tool_use_id
                ? { ...tc, output: result.output, status: "completed" as const }
                : tc,
            );
            return [...prev.slice(0, -1), { ...last, toolCalls: updated }];
          }
          return prev;
        });
      } else if ("ToolApprovalRequest" in event) {
        const req = event.ToolApprovalRequest;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && last.toolCalls) {
            const exists = last.toolCalls.some((tc) => tc.toolUseId === req.tool_use_id);
            if (exists) {
              const updated = last.toolCalls.map((tc) =>
                tc.toolUseId === req.tool_use_id ? { ...tc, needsApproval: true } : tc,
              );
              return [...prev.slice(0, -1), { ...last, toolCalls: updated }];
            }
          }
          const toolCall: ToolCall = {
            toolUseId: req.tool_use_id,
            toolName: req.tool_name,
            input: req.input,
            status: "pending",
            needsApproval: true,
          };
          if (last?.role === "assistant") {
            return [
              ...prev.slice(0, -1),
              { ...last, toolCalls: [...(last.toolCalls ?? []), toolCall] },
            ];
          }
          return [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: "assistant",
              content: "",
              toolCalls: [toolCall],
              timestamp: Date.now(),
            },
          ];
        });
      } else if ("MemoryConfirmationRequest" in event) {
        const req = event.MemoryConfirmationRequest;
        setMemoryConfirmations((prev) => [
          ...prev,
          { toolUseId: req.tool_use_id, action: req.action, input: req.input },
        ]);
      } else if ("TurnComplete" in event) {
        const turn = event.TurnComplete;
        totalCostRef.current += turn.cost_usd;
        totalDurationRef.current += turn.duration_ms;
        const ctxUsed =
          turn.input_tokens +
          turn.output_tokens +
          turn.cache_read_tokens +
          turn.cache_creation_tokens;
        ctxUsedRef.current = ctxUsed;

        const ctxWindow = agent.contextWindow ?? 200_000;
        const pct = ((ctxUsedRef.current / ctxWindow) * 100).toFixed(1);
        const text = costTpl
          .replace("{cost}", formatCost(turn.cost_usd))
          .replace("{totalCost}", formatCost(totalCostRef.current))
          .replace("{duration}", formatDuration(turn.duration_ms, locale))
          .replace("{pct}", pct)
          .replace("{usedTokens}", formatTokens(ctxUsedRef.current))
          .replace("{ctxWindow}", formatTokens(ctxWindow));

        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return [...prev.slice(0, -1), { ...last, costInfo: text }];
          }
          return prev;
        });
      } else if ("Error" in event) {
        const err = event.Error;
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: `**Error:** ${err.message}`,
            timestamp: Date.now(),
          },
        ]);
        onError?.({ kind: "agent_event", message: err.message });
      } else if ("Started" in event) {
        // 模型切换后新 adapter 启动，重置 exited 状态
        setExited(false);
      } else if ("Exited" in event) {
        const ex = event.Exited;
        setExited(true);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "system",
            content: ex.reason ?? "",
            systemKind: "exited",
            timestamp: Date.now(),
          },
        ]);
        onAgentExited?.(sessionIdRef.current, ex.reason);
      }
    },
    [agent.contextWindow, costTpl, locale, onAgentExited, onError],
  );

  useEffect(() => {
    if (!sessionId) return;
    return transport.subscribeEvents(sessionId, handleEvent);
  }, [sessionId, transport, handleEvent]);

  // ─── 消息送达确认 (✓/✓✓) ───
  useEffect(() => {
    if (!transport.onMessageAck) return;
    return transport.onMessageAck((ack) => {
      setMessages((prev) => prev.map((m) =>
        m.msgId === ack.msg_id ? { ...m, deliveryStatus: ack.status } : m
      ));
    });
  }, [transport]);

  // ─── 多 session 后台追踪 ───
  const bgCacheRef = useRef<Map<string, { messages: ChatMessage[]; todos: TodoItem[] }>>(new Map());
  const unreadRef = useRef<UnreadState>({ counts: {}, activity: {} });
  const onUnreadChangeRef = useRef(onUnreadChange);
  onUnreadChangeRef.current = onUnreadChange;

  useEffect(() => {
    if (!allSessionIds || allSessionIds.length === 0) return;

    const bgSessionIds = allSessionIds.filter((id) => id !== sessionId);
    if (bgSessionIds.length === 0) return;

    const unsubs: Array<() => void> = [];
    for (const bgSid of bgSessionIds) {
      const unsub = transport.subscribeEvents(bgSid, (event: AgentEvent) => {
        if (typeof event === "string") return;

        // 更新后台缓存
        const cache = bgCacheRef.current.get(bgSid) ?? { messages: [], todos: [] };
        cache.messages = applyEventToMessages(cache.messages, event);

        if ("TodoUpdate" in event) {
          cache.todos = event.TodoUpdate.todos.map((td: any) => ({
            id: td.id, content: td.content,
            status: (td.status === "completed" ? "completed" : td.status === "in_progress" ? "in_progress" : "pending"),
            priority: td.priority,
          }));
        }

        bgCacheRef.current.set(bgSid, cache);

        // 更新未读状态
        const ur = unreadRef.current;
        if ("TextDelta" in event || "MessageComplete" in event) {
          ur.counts[bgSid] = (ur.counts[bgSid] || 0) + 1;
          ur.activity[bgSid] = true;
        } else if ("ToolUse" in event || "ThinkingDelta" in event) {
          ur.activity[bgSid] = true;
        } else if ("TurnComplete" in event) {
          ur.activity[bgSid] = false;
        }

        onUnreadChangeRef.current?.({ ...ur, counts: { ...ur.counts }, activity: { ...ur.activity } });
      });
      unsubs.push(unsub);
    }

    return () => unsubs.forEach((u) => u());
  }, [allSessionIds?.join(","), sessionId, transport]);

  // 切换 session 时：从后台缓存恢复 + 清零未读
  useEffect(() => {
    if (!sessionId) return;
    const bgCache = bgCacheRef.current.get(sessionId);
    if (bgCache && bgCache.messages.length > 0) {
      // 有后台缓存：合并到当前消息（后台缓存的是切出后收到的新消息）
      setMessages((prev) => {
        if (prev.length === 0) return bgCache.messages;
        // 合并：保留 prev（DB 或缓存恢复），追加后台期间的新消息
        // 用 timestamp 判断哪些是新的
        const lastPrevTs = prev[prev.length - 1]?.timestamp ?? 0;
        const newMsgs = bgCache.messages.filter((m) => m.timestamp > lastPrevTs);
        return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
      });
      if (bgCache.todos.length > 0) setTodos(bgCache.todos);
      bgCacheRef.current.delete(sessionId);
    }
    // 清零该 session 的未读
    const ur = unreadRef.current;
    delete ur.counts[sessionId];
    delete ur.activity[sessionId];
    onUnreadChangeRef.current?.({ ...ur, counts: { ...ur.counts }, activity: { ...ur.activity } });
  }, [sessionId]);

  // ─── 操作 ───
  const send = useCallback(
    (content: string, attachments?: string[]) => {
      if (!content.trim() || !sessionId) return;
      const userMsgId = crypto.randomUUID();
      setMessages((prev) => [
        ...prev,
        {
          id: userMsgId,
          role: "user",
          content: content.trim(),
          timestamp: Date.now(),
          deliveryStatus: "sending",
        },
      ]);
      try {
        const wsMsgId = transport.sendMessage(agentId, sessionId, content.trim(), attachments);
        if (wsMsgId) {
          setMessages((prev) => prev.map((m) =>
            m.id === userMsgId ? { ...m, msgId: wsMsgId } : m
          ));
        }
      } catch (e) {
        onError?.({
          kind: "transport",
          cause: e instanceof Error ? e : new Error(String(e)),
        });
      }
    },
    [agentId, sessionId, transport, onError],
  );

  const approveTool = useCallback(
    (toolUseId: string, approved: boolean) => {
      if (!sessionId) return;
      try {
        transport.sendToolApproval(agentId, sessionId, toolUseId, approved);
      } catch (e) {
        onError?.({
          kind: "transport",
          cause: e instanceof Error ? e : new Error(String(e)),
        });
        return;
      }
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && last.toolCalls) {
          const updated = last.toolCalls.map((tc) =>
            tc.toolUseId === toolUseId
              ? {
                  ...tc,
                  status: approved ? ("approved" as const) : ("denied" as const),
                  needsApproval: false,
                }
              : tc,
          );
          return [...prev.slice(0, -1), { ...last, toolCalls: updated }];
        }
        return prev;
      });
    },
    [agentId, sessionId, transport, onError],
  );

  const approveMemory = useCallback(
    (toolUseId: string, approved: boolean) => {
      if (!sessionId) return;
      try {
        transport.sendToolApproval(agentId, sessionId, toolUseId, approved);
      } catch (e) {
        onError?.({
          kind: "transport",
          cause: e instanceof Error ? e : new Error(String(e)),
        });
      }
      setMemoryConfirmations((prev: MemoryConfirmation[]) => prev.filter((m: MemoryConfirmation) => m.toolUseId !== toolUseId));
    },
    [agentId, sessionId, transport, onError],
  );

  const interrupt = useCallback(() => {
    if (!sessionId) return;
    try {
      transport.interrupt(agentId, sessionId);
    } catch (e) {
      onError?.({
        kind: "transport",
        cause: e instanceof Error ? e : new Error(String(e)),
      });
    }
  }, [agentId, sessionId, transport, onError]);

  const loadMoreHistory = useCallback(() => {
    if (!hasMoreHistory || loadingHistory || !sessionId) return;
    setLoadingHistory(true);
    const oldest = messages[0]?.created_at;
    const before = oldest ? new Date(oldest).toISOString() : undefined;
    try {
      transport.queryMessages(agentId, sessionId, { before });
    } catch (e) {
      setLoadingHistory(false);
      onError?.({
        kind: "history",
        cause: e instanceof Error ? e : new Error(String(e)),
      });
    }
  }, [hasMoreHistory, loadingHistory, messages, agentId, sessionId, transport, onError]);

  return {
    messages,
    todos,
    memoryConfirmations,
    hasMoreHistory,
    loadingHistory,
    firstItemIndex,
    exited,
    send,
    approveTool,
    approveMemory,
    interrupt,
    loadMoreHistory,
  };
}
